// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Atividades.tsx
//  CRM → Atividades (agenda). Porte do FrmCadAtividade / FrmAgendaAtividades.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CalendarClock } from 'lucide-react';

import { useAtividades } from '@/features/operacional/hooks/useAtividades';
import { BarraFiltros } from '@/components/BarraFiltros';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { UsuarioSearchCombobox } from '@/components/UsuarioSearchCombobox';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import type { TamanhoPagina } from '@/hooks/usePaginacaoLocal';
import { useNomesUsuarios } from '@/hooks/useNomesUsuarios';
import {
  ATIVIDADE_TIPOS, ATIVIDADE_PRIORIDADES, ATIVIDADE_STATUS,
} from '@/features/operacional/types';
import type { AtividadeFiltros, AtividadeResumo, CriarAtividadeRequest } from '@/features/operacional/types';
import { atividadeService } from '@/features/operacional/services/atividadeService';
import { getApiErrorMessage } from '@/lib/apiClient';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { PeriodoFiltro, usePeriodo, type Periodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_COR: Record<number, string> = {
  0: 'bg-amber-500/10 text-amber-600',     // Pendente
  1: 'bg-blue-500/10 text-blue-600',       // Em andamento
  2: 'bg-emerald-500/10 text-emerald-600', // Concluída
  3: 'bg-zinc-500/10 text-zinc-500',       // Cancelada
};
const PRIORIDADE_COR: Record<number, string> = {
  0: 'text-zinc-500', 1: 'text-blue-600', 2: 'text-amber-600', 3: 'text-rose-600',
};

const SENTINELA = '__none__';

type OrdemAtividades = NonNullable<AtividadeFiltros['ordenarPor']>;

/**
 * Períodos "últimos N dias / este mês" terminam hoje, mas numa agenda as atividades AGENDADAS
 * (início futuro) importam tanto quanto as passadas: nesses períodos a lista vai do início do
 * período em diante, sem limite final. "Hoje" e "Personalizado" respeitam o intervalo exato.
 */
const PERIODOS_COM_AGENDADAS: Periodo[] = ['7dias', '30dias', 'mes'];

function paraInputLocal(iso?: string | null) {
  if (!iso) return '';
  return iso.slice(0, 16); // "YYYY-MM-DDTHH:mm"
}
function formatarData(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

interface FormState {
  id?: number;
  tipo: number;
  titulo: string;
  clienteId: number | null;
  responsavelId: number | '';
  dtInicio: string;
  dtFim: string;
  isDiaInteiro: boolean;
  prioridade: number;
  status: number;
  dtLembrete: string;
  descricao: string;
  resultado: string;
}

const FORM_VAZIO: FormState = {
  tipo: 0, titulo: '', clienteId: null, responsavelId: '',
  dtInicio: '', dtFim: '', isDiaInteiro: false, prioridade: 1, status: 0,
  dtLembrete: '', descricao: '', resultado: '',
};

const Atividades = () => {
  // FrmAgendaAtividades abre na semana: sem período a lista traria o histórico inteiro.
  const [filtroStatus, setFiltroStatus] = useState<string>(SENTINELA);
  const [filtroResponsavel, setFiltroResponsavel] = useState(0);
  const [filtroCliente, setFiltroCliente] = useState(0);
  // A BarraFiltros já espera a digitação parar antes de avisar: a busca vai direto ao servidor.
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<OrdemAtividades>('inicio', true, () => setPagina(1));
  const incluiAgendadas = PERIODOS_COM_AGENDADAS.includes(periodo.periodo);

  const filtros: AtividadeFiltros = {
    ...(filtroStatus !== SENTINELA ? { status: Number(filtroStatus) } : {}),
    ...(filtroResponsavel ? { responsavelId: filtroResponsavel } : {}),
    ...(filtroCliente ? { clienteId: filtroCliente } : {}),
    busca: busca || undefined,
    dtInicio: periodo.intervalo.dtInicio,
    dtFim: incluiAgendadas ? undefined : periodo.intervalo.dtFim,
    ordenarPor: ordenacao.ordenarPor,
    decrescente: ordenacao.decrescente,
  };
  const filtrosAtivos = [
    filtroStatus !== SENTINELA, !!filtroResponsavel, !!filtroCliente, !!busca,
  ].filter(Boolean).length;
  const limparFiltros = () => {
    setFiltroStatus(SENTINELA); setFiltroResponsavel(0); setFiltroCliente(0); setBusca('');
    periodo.setPeriodo('30dias');
  };
  const { query, create, update, remove } = useAtividades(filtros, pagina, tamanhoPagina);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(FORM_VAZIO);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);
  const editando = form.id != null;

  const abrirNovo = () => { setForm(FORM_VAZIO); setDialogOpen(true); };

  const abrirEdicao = async (a: AtividadeResumo) => {
    const det = await atividadeService.getById(a.id);
    setForm({
      id: det.id, tipo: det.tipo, titulo: det.titulo, clienteId: det.clienteId ?? null,
      responsavelId: det.responsavelId, dtInicio: paraInputLocal(det.dtInicio),
      dtFim: paraInputLocal(det.dtFim), isDiaInteiro: det.isDiaInteiro,
      prioridade: det.prioridade, status: det.status, dtLembrete: paraInputLocal(det.dtLembrete),
      descricao: det.descricao ?? '', resultado: det.resultado ?? '',
    });
    setDialogOpen(true);
  };

  const salvar = () => {
    if (!form.titulo.trim()) { toast.error('Título é obrigatório.'); return; }
    if (!form.dtInicio) { toast.error('Data de início é obrigatória.'); return; }
    if (form.responsavelId === '') { toast.error('Responsável é obrigatório.'); return; }

    const req: CriarAtividadeRequest = {
      titulo: form.titulo,
      dtInicio: form.dtInicio,
      responsavelId: Number(form.responsavelId),
      tipo: form.tipo,
      clienteId: form.clienteId,
      dtFim: form.dtFim || null,
      isDiaInteiro: form.isDiaInteiro,
      prioridade: form.prioridade,
      status: form.status,
      dtLembrete: form.dtLembrete || null,
      descricao: form.descricao || null,
      resultado: form.resultado || null,
    };

    const onOk = () => { toast.success(editando ? 'Atividade atualizada.' : 'Atividade criada.'); setDialogOpen(false); };
    const onErr = (e: unknown) => toast.error(getApiErrorMessage(e));

    if (editando) update.mutate({ id: form.id!, req }, { onSuccess: onOk, onError: onErr });
    else create.mutate(req, { onSuccess: onOk, onError: onErr });
  };

  const excluir = (id: number) => {
    remove.mutate(id, {
      onSuccess: () => toast.success('Atividade removida.'),
      onError: (e) => toast.error(getApiErrorMessage(e)),
    });
  };

  const atividades   = query.data?.itens ?? [];
  const total        = query.data?.total ?? 0;
  const totalPaginas = Math.max(1, query.data?.totalPaginas ?? 1);
  const { nome: nomeUsuario } = useNomesUsuarios(atividades.map((a) => a.responsavelId));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="h-6 w-6" />
            Atividades
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Agenda de tarefas, reuniões, ligações e follow-ups.
          </p>
        </div>
        <Button onClick={abrirNovo} className="gap-2">
          <Plus className="h-4 w-4" /> Nova Atividade
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <BarraFiltros
            busca={busca}
            onBuscar={(t) => { setBusca(t); setPagina(1); }}
            placeholderBusca="Título, descrição ou cliente..."
            filtrosAtivos={filtrosAtivos}
            onLimpar={limparFiltros}
          >
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Período (início)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <PeriodoFiltro estado={periodo} rotuloData="Início" />
              </div>
              {incluiAgendadas && (
                <p className="text-xs text-muted-foreground">Inclui as atividades agendadas para os próximos dias.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={filtroStatus} onValueChange={(v) => { setFiltroStatus(v); setPagina(1); }}>
                <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SENTINELA}>Todos</SelectItem>
                  {ATIVIDADE_STATUS.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[240px] space-y-1">
              <Label className="text-xs text-muted-foreground">Responsável</Label>
              <UsuarioSearchCombobox value={filtroResponsavel}
                onChange={(id) => { setFiltroResponsavel(id); setPagina(1); }}
                placeholder="Todos os responsáveis" />
            </div>
            <div className="w-[260px] space-y-1">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <PessoaSearchCombobox value={filtroCliente || undefined}
                onChange={(id) => { setFiltroCliente(id); setPagina(1); }}
                filter="cliente" placeholder="Todos os clientes" />
            </div>
          </BarraFiltros>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="pt-4">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : atividades.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {periodo.periodo !== 'todos'
                ? 'Nenhuma atividade encontrada neste período — amplie o período (ex.: Todo o período) para ver as anteriores.'
                : 'Nenhuma atividade encontrada.'}
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Tipo</TableHead>
                    <CabecalhoOrdenavel coluna="titulo" ordenacao={ordenacao} decrescentePadrao={false}>Título</CabecalhoOrdenavel>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Responsável</TableHead>
                    <CabecalhoOrdenavel coluna="inicio" ordenacao={ordenacao} className="w-40">Início</CabecalhoOrdenavel>
                    <CabecalhoOrdenavel coluna="prioridade" ordenacao={ordenacao} className="w-28">Prioridade</CabecalhoOrdenavel>
                    <CabecalhoOrdenavel coluna="status" ordenacao={ordenacao} decrescentePadrao={false} className="w-32">Status</CabecalhoOrdenavel>
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {atividades.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell><Badge variant="secondary">{ATIVIDADE_TIPOS[a.tipo] ?? a.tipo}</Badge></TableCell>
                      <TableCell className="font-medium">{a.titulo}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.clienteNome ?? '—'}</TableCell>
                      <TableCell className="text-sm">{nomeUsuario(a.responsavelId)}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{formatarData(a.dtInicio)}</TableCell>
                      <TableCell className={`text-sm font-medium ${PRIORIDADE_COR[a.prioridade] ?? ''}`}>
                        {ATIVIDADE_PRIORIDADES[a.prioridade] ?? a.prioridade}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={STATUS_COR[a.status] ?? ''}>
                          {ATIVIDADE_STATUS[a.status] ?? a.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => abrirEdicao(a)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Excluir atividade"
                            onClick={() => setExcluindoId(a.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {total > 0 && (
            <div className="mt-4">
              <PaginacaoRodape
                pagina={pagina}
                totalPaginas={totalPaginas}
                totalItens={total}
                primeiroItem={(pagina - 1) * tamanhoPagina + 1}
                ultimoItem={Math.min(pagina * tamanhoPagina, total)}
                tamanhoPagina={tamanhoPagina}
                onPaginaChange={setPagina}
                onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
              />
              {query.isFetching && !query.isLoading && (
                <p className="text-xs text-muted-foreground mt-1">(atualizando...)</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={excluindoId != null}
        onOpenChange={(o) => !o && setExcluindoId(null)}
        onConfirm={() => { if (excluindoId) excluir(excluindoId); setExcluindoId(null); }}
        title="Excluir atividade"
        description="Tem certeza que deseja remover esta atividade?"
      />

      {/* Dialog form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Atividade' : 'Nova Atividade'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={String(form.tipo)} onValueChange={(v) => setForm((f) => ({ ...f, tipo: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATIVIDADE_TIPOS.map((t, i) => <SelectItem key={i} value={String(i)}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Prioridade</Label>
              <Select value={String(form.prioridade)} onValueChange={(v) => setForm((f) => ({ ...f, prioridade: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATIVIDADE_PRIORIDADES.map((p, i) => <SelectItem key={i} value={String(i)}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 sm:col-span-2">
              <Label>Título *</Label>
              <Input value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                maxLength={120} placeholder="Ex.: Ligar para o cliente sobre a proposta" />
            </div>

            <div className="space-y-1">
              <Label>Cliente</Label>
              <PessoaSearchCombobox
                value={form.clienteId}
                onChange={(id) => setForm((f) => ({ ...f, clienteId: id }))}
                placeholder="Opcional..."
                filter="cliente"
              />
            </div>
            <div className="space-y-1">
              <Label>Responsável *</Label>
              <UsuarioSearchCombobox value={form.responsavelId === '' ? undefined : form.responsavelId}
                onChange={(id) => setForm((f) => ({ ...f, responsavelId: id || '' }))}
                placeholder="Selecione o responsável..." />
            </div>

            <div className="space-y-1">
              <Label>Início *</Label>
              <Input type="datetime-local" value={form.dtInicio}
                onChange={(e) => setForm((f) => ({ ...f, dtInicio: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Fim</Label>
              <Input type="datetime-local" value={form.dtFim}
                onChange={(e) => setForm((f) => ({ ...f, dtFim: e.target.value }))} />
            </div>

            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={String(form.status)} onValueChange={(v) => setForm((f) => ({ ...f, status: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATIVIDADE_STATUS.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Lembrete</Label>
              <Input type="datetime-local" value={form.dtLembrete}
                onChange={(e) => setForm((f) => ({ ...f, dtLembrete: e.target.value }))} />
            </div>

            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox id="dia-inteiro" checked={form.isDiaInteiro}
                onCheckedChange={(v) => setForm((f) => ({ ...f, isDiaInteiro: !!v }))} />
              <Label htmlFor="dia-inteiro" className="font-normal cursor-pointer">Dia inteiro</Label>
            </div>

            <div className="space-y-1 sm:col-span-2">
              <Label>Descrição</Label>
              <Textarea value={form.descricao} rows={3}
                onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} />
            </div>
            {editando && (
              <div className="space-y-1 sm:col-span-2">
                <Label>Resultado</Label>
                <Textarea value={form.resultado} rows={2}
                  onChange={(e) => setForm((f) => ({ ...f, resultado: e.target.value }))} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={create.isPending || update.isPending}>
              {editando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Atividades;
